<template>
    <span>
        <div class="page">
            <div class="grid grid-cols-2 gap-2">
                <div class="flex flex-col gap-2 ">
                    <div class="flex flex-col items-center justify-center h-[530px] card-css relative p-4">
                        <form ref="formRef" class="w-full flex flex-col gap-4">
                            <!-- Template Selection -->
                            <div class="flex flex-col gap-2 w-full">
                                <FieldLabelWithTooltip
                                    wrapper-class="text-sm font-medium mb-1"
                                    :label="$t('gm.dashboards.template')"
                                    :tooltip="$t('gm.dashboards.template_tooltip')"
                                />
                                <div v-if="ipadSettingWarning" class="p-3 mb-2 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
                                    <h3 class="font-semibold">{{ $t('dialogs.warning_change_template') }}</h3>
                                </div>
                                <Dropdown 
                                    v-model="storeEvents.activityConfiguration.ipad_settings"
                                    :options="ipadSettings"
                                    :placeholder="$t('gm.dashboards.placeholder_template')"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="w-full"
                                    @update:modelValue="setIpadSettings"
                                />
                                <small v-if="storeEvents.activityConfiguration.ipad_settings == '0'" class="text-yellow-600">
                                    {{ $t('gm.dashboards.warning_template') }}
                                </small>
                            </div>

                            <!-- Game Type -->
                            <div class="flex flex-col gap-2 w-full">
                                <FieldLabelWithTooltip
                                    wrapper-class="text-sm font-medium mb-1"
                                    :label="$t('gm.dashboards.type_game')"
                                    :tooltip="$t('gm.dashboards.type_game_tooltip')"
                                    :required="true"
                                />
                                <div class="flex gap-4">
                                    <div v-for="type in typeEvent" :key="type.value" class="flex items-center gap-1">
                                        <RadioButton 
                                            :inputId="'type_' + type.value"
                                            :value="type.value"
                                            v-model="storeEvents.activityConfiguration.type"
                                        />
                                        <label :for="'type_' + type.value" class="ml-2 cursor-pointer">
                                            {{ type.label }}
                                        </label>
                                        <i
                                            v-if="type.tooltip"
                                            class="pi pi-question-circle text-sm text-primary cursor-help shrink-0"
                                            v-tooltip.top="{
                                                value: type.tooltip,
                                                showDelay: 200,
                                                hideDelay: 0,
                                                tooltipStyleClass: 'max-w-md whitespace-normal break-words',
                                            }"
                                            @click.stop.prevent
                                            @mousedown.stop.prevent
                                            @pointerdown.stop
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Map configuration -->
                            <div
                                v-if="storeEvents.activityConfiguration.type === 'MAP' && storeEvents.activityConfiguration.ipad_settings && storeEvents.activityConfiguration.ipad_settings !== '0'"
                                class="flex flex-col gap-2 w-full"
                            >
                                <label class="text-sm font-medium mb-1">{{ $t('map_config.select') }}</label>
                                <Select
                                    v-model="selectedConfigId"
                                    :options="configOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="w-full"
                                    :loading="loadingConfigs"
                                    :placeholder="$t('map_config.select')"
                                    append-to="body"
                                    :focus-on-hover="false"
                                    @change="onMapConfigSelected"
                                />
                            </div>

                            <!-- Latitude, Longitude, and Locate Button -->
                            <div v-if="storeEvents.activityConfiguration.type === 'MAP'" class="flex flex-col gap-2 w-full">
                                <FieldLabelWithTooltip
                                    wrapper-class="text-sm font-medium mb-1"
                                    :label="$t('gm.dashboards.initial_position')"
                                    :tooltip="$t('gm.dashboards.initial_position_tooltip')"
                                />
                                <div class="grid grid-cols-12 gap-4">
                                <div class="col-span-5 flex flex-col gap-2">
                                    <label class="text-sm font-medium mb-1">
                                        {{ $t('gm.dashboards.latitude') }}
                                        <span v-if="requiresInitialPosition" class="text-red-500">*</span>
                                    </label>
                                    <InputNumber 
                                        v-model="storeEvents.activityConfiguration.latitude"
                                        :disabled="true"
                                        :minFractionDigits="0"
                                        :maxFractionDigits="12"
                                        :class="['w-full', { 'p-invalid': errors.latitude }]"
                                    />
                                    <small v-if="errors.latitude" class="p-error">{{ errors.latitude }}</small>
                                </div>
                                <div class="col-span-5 flex flex-col gap-2">
                                    <label class="text-sm font-medium mb-1">
                                        {{ $t('gm.dashboards.longitude') }}
                                        <span v-if="requiresInitialPosition" class="text-red-500">*</span>
                                    </label>
                                    <InputNumber 
                                        v-model="storeEvents.activityConfiguration.longitude"
                                        :disabled="true"
                                        :minFractionDigits="0"
                                        :maxFractionDigits="12"
                                        :class="['w-full', { 'p-invalid': errors.longitude }]"
                                    />
                                    <small v-if="errors.longitude" class="p-error">{{ errors.longitude }}</small>
                                </div>
                                <div class="col-span-2 flex items-end">
                                    <Button 
                                        v-if="!addMarkerActive"
                                        icon="pi pi-map-marker"
                                        severity="secondary"
                                        text
                                        :disabled="isConfigLocked"
                                        @click="addMarkerActive = true"
                                        v-tooltip="$t('gm.dashboards.initial_position')"
                                    />
                                    <Button 
                                        v-else
                                        icon="pi pi-map-marker"
                                        severity="primary"
                                        @click="addMarkerActive = false"
                                        v-tooltip="$t('gm.dashboards.initial_position')"
                                    />
                                </div>
                                </div>
                            </div>

                            <!-- Duration -->
                            <div class="flex flex-col gap-2 w-full">
                                <label class="text-sm font-medium mb-1">
                                    {{ $t('gm.dashboards.duration_seconds') }}
                                    <span class="text-red-500">*</span>
                                </label>
                                <InputNumber 
                                    v-model="durationMinutes"
                                    :min="1"
                                    :class="['w-full', { 'p-invalid': errors.duration }]"
                                    @update:modelValue="clearError('duration')"
                                />
                                <small v-if="errors.duration" class="p-error">{{ errors.duration }}</small>
                            </div>
                        </form>
                    </div>
                </div>
                <div  class="relative flex w-full justify-center h-full" :class="{'hidden': storeEvents.activityConfiguration.type !== 'MAP'}">
                    <input type="search" ref="addressInput" class="controls absolute mb-2 bottom-0 rounded-lg border-2 border-teal-400 p-2 z-20 w-9/12" :placeholder="$t('gm.dashboards.placeholder_search_address')" />
                    <div ref="mapContainer" class="flex flex-col card-css w-full"></div>
                    <MapConfigLockOverlay :visible="isConfigLocked" />
                </div>
            </div>
        </div>
    </span>
</template>

<script lang="ts" setup>
import Button from "primevue/button"
import InputNumber from "primevue/inputnumber"
import Dropdown from "primevue/dropdown"
import Select from "primevue/select"
import RadioButton from "primevue/radiobutton"
import { shallowRef, ref, onMounted, toRaw, computed, watch } from "vue"
import { useRoute } from "vue-router"
import { fetchPromiseAll } from "@/utils/http";
import { useEventsStore } from "@/stores/apps/events"
import { Loader } from "@googlemaps/js-api-loader"
import { useContentStore } from "@/stores/apps/content"
import { useIpadHuntStore } from "@/stores/apps/ipad_hunt"
import { useStoreI18n } from "@/composables/useStoreI18n"
import { useAuthStore } from "@/stores/auth"
import { useMapConfig } from "@/composables/useMapConfig"
import MapConfigLockOverlay from "@/components/mapConfig/MapConfigLockOverlay.vue"
import FieldLabelWithTooltip from "@/components/common/FieldLabelWithTooltip.vue"

const mapConfig = useMapConfig("ipad_hunt")
const {
    selectedConfigId,
    configOptions,
    loadingConfigs,
    isConfigLocked,
    fetchConfigs,
    applyCenterToActivityConfiguration,
    setSelectedConfigId,
    configs,
    selectedConfig,
} = mapConfig

const { t } = useStoreI18n()
const storeContent = useContentStore()
const storeIpad = useIpadHuntStore()
const storeEvents = useEventsStore()

const typeEvent = ref([
    { label: t("gm.dashboards.qr_upper"), value: "QR", tooltip: t("gm.dashboards.qr_upper_tooltip") },
    { label: t("gm.dashboards.map"), value: "MAP", tooltip: t("gm.dashboards.map_tooltip") },
])

const templateOldValue = ref(null)
const errors = ref<Record<string, string>>({})

const durationMinutes = ref(0)
watch(durationMinutes, (newVal) => {
    storeEvents.activityConfiguration.duration = (newVal ?? 0) * 60
})

const hasTemplateWithInitialPosition = computed(() => {
    const config = selectedConfig.value
    if (!config?.center) return false
    return config.center.latitude != null && config.center.longitude != null
})

const requiresInitialPosition = computed(() => {
    return storeEvents.activityConfiguration.type === "MAP" && !hasTemplateWithInitialPosition.value
})

const clearError = (fieldName: string) => {
    if (errors.value[fieldName]) {
        delete errors.value[fieldName]
    }
}

const validateForm = (callback: (validationErrors: Record<string, string> | null) => void) => {
    const config = storeEvents.activityConfiguration as any
    const validationErrors: Record<string, string> = {}
    errors.value = {}

    if (config.duration == null || config.duration <= 0 || !durationMinutes.value || durationMinutes.value <= 0) {
        const errorMsg = t("gm.dashboards.put_duration")
        validationErrors.duration = errorMsg
        errors.value.duration = errorMsg
    }

    if (config.type === "MAP") {
        const lat = config.latitude
        const lng = config.longitude
        const hasCoords =
            lat != null &&
            lng != null &&
            !Number.isNaN(Number(lat)) &&
            !Number.isNaN(Number(lng))

        // Required unless a map config/template already provided the initial point
        if (!hasCoords && !hasTemplateWithInitialPosition.value) {
            const latError = t("gm.dashboards.select_latitude")
            const lngError = t("gm.dashboards.select_longitude")
            validationErrors.latitude = latError
            validationErrors.longitude = lngError
            errors.value.latitude = latError
            errors.value.longitude = lngError
        }
    }

    callback(Object.keys(validationErrors).length > 0 ? validationErrors : null)
}

const createFormRef = () => ({
    validate: validateForm,
})

const route = useRoute()

const map = shallowRef<google.maps.Map | null>(null);
const mapContainer = ref<HTMLDivElement | null>(null);

const addMarkerActive = ref(false)
const initMarker = ref(null)

const addMarker = async (pos) => 
{
    const { InfoWindow } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");
    const infoWindow = new InfoWindow();

    const pinElement = new PinElement({
        glyph: t('gm.dashboards.initial_position'),
        background: 'red',
        borderColor: 'black',
        scale:1
    })
    const draggableMarker  = new AdvancedMarkerElement({
        position: pos,
        title: t('gm.dashboards.initial_position'),
        map: toRaw(map.value),
        gmpDraggable: true,
        content: pinElement.element,
    })

    draggableMarker.addListener('dragend', (event) => {
        let position = draggableMarker.position as google.maps.LatLng;
        infoWindow.close();
        storeEvents.activityConfiguration.latitude = position.lat
        storeEvents.activityConfiguration.longitude = position.lng
        clearError("latitude")
        clearError("longitude")
    });
    initMarker.value = draggableMarker
}

const initIpadSetting = ref(null)
const ipadSettingWarning = ref(false)
const setIpadSettings = async () => 
{
    if(route.params.api_key !== undefined)
    {
        if(storeEvents.activityConfiguration.ipad_settings == initIpadSetting.value)
        {
            ipadSettingWarning.value = false
        }
        else
        {
            ipadSettingWarning.value = true
        }
    }
    setSelectedConfigId(null)
    storeEvents.activityConfiguration.map_config_id = null
    await fetchConfigs(storeEvents.activityConfiguration.ipad_settings)
}

const onMapConfigSelected = async (event: { value: string | null }) => {
    const configId = event.value ?? null
    storeEvents.activityConfiguration.map_config_id = configId
    if (!configId) {
        setSelectedConfigId(null)
        return
    }
    const config = configs.value.find((item) => item.id === configId)
    if (!config) return
    applyCenterToActivityConfiguration(config, storeEvents.activityConfiguration)
    setSelectedConfigId(configId)
    if (config.center?.latitude != null && config.center?.longitude != null) {
        const lat = Number(config.center.latitude)
        const lng = Number(config.center.longitude)
        clearError("latitude")
        clearError("longitude")
        if (initMarker.value != null) {
            await deleteMarker()
        }
        await addMarker({ lat, lng })
        map.value?.setCenter({ lat, lng })
        map.value?.setZoom(15)
    }
}


const firstSave = ref(false)

const addressInput = ref(null);
async function loadMap() {
  const loader = new Loader({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    version: 'weekly',
    libraries: ["places"],
  });

  loader.load().then( async () => {
    if (mapContainer.value) {
        const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
        const Places = await loader.importLibrary("places") as google.maps.PlacesLibrary;
        // Get default center from organization initial position or fallback to hardcoded value
        const authStore = useAuthStore()
        const org = (authStore.full_user as any)?.organization
        let center = { lat: 40.374189, lng: 2.2847488443187474 }
        
        // Use organization initial position if available
        if (org?.initial_position_latitude != null && org?.initial_position_longitude != null) {
            const lat = Number(org.initial_position_latitude)
            const lng = Number(org.initial_position_longitude)
            // Only use if both are valid numbers
            if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
                center = { lat: lat, lng: lng }
            }
        }
        
        // Override with activity configuration if set
        if( storeEvents.activityConfiguration.latitude !== null)
        {
            const configLat = Number(storeEvents.activityConfiguration.latitude)
            const configLng = Number(storeEvents.activityConfiguration.longitude)
            if (!isNaN(configLat) && !isNaN(configLng) && isFinite(configLat) && isFinite(configLng)) {
                center = { lat: configLat, lng: configLng }
            }
        }
        map.value = new Map(mapContainer.value, {
            center: center,
            zoom: 6,
            mapId: "4504f8b37365c3d0"
        });

        // Create a bounding box with sides ~10km away from the center point
        const defaultBounds = {
            north: center.lat + 0.1,
            south: center.lat - 0.1,
            east: center.lng + 0.1,
            west: center.lng - 0.1,
        };

        const options = {
            bounds: defaultBounds, //optional
            types: [], //optioanl //limiter for the places api search
            fields: ["address_components", "geometry", "icon", "name"], //allows the api to accept these inputs and return similar ones
            strictBounds: false, //optional
        };

        const autocomplete = new Places.Autocomplete(addressInput.value, options);

        console.log('autocomplete', autocomplete);

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace(); //this callback is inherent you will see it if you logged autocomplete
            map.value.setCenter(new google.maps.LatLng(place.geometry.location.lat(), place.geometry.location.lng()));
            map.value.setZoom(18)
            console.log('place', place);
        });

        map.value.addListener("click", (mapsMouseEvent) => {
            placeMarker({lat: mapsMouseEvent.latLng.lat(), lng: mapsMouseEvent.latLng.lng()})
        });
    } else {
      console.error("Map container element is null.");
    }
  });
}

const deleteMarker = async () => {
    initMarker.value.setMap(null)
}

const placeMarker = async (pos) => {
    if (isConfigLocked.value) return
    if(addMarkerActive.value)
    {
        if(initMarker.value != null)
        {
            deleteMarker()
        }
        addMarker(pos)
        storeEvents.activityConfiguration.latitude = pos.lat
        storeEvents.activityConfiguration.longitude = pos.lng
        clearError("latitude")
        clearError("longitude")
        addMarkerActive.value = false
    }
}

const ipadSettings = computed(() => { 
    if(storeContent.content.ipad_settings != undefined) 
    {
        let is = storeContent.content.ipad_settings
        is.unshift({id: "0", name: t('gm.dashboards.not_template')})
        return is.map((v) => ({ value: v.id.toString(), label: v.name }))
    }
    else
    {
        return [{value:"0", label: t('gm.dashboards.not_template')}]
    }
})

const loadIpadForm = () => 
{
    if(storeIpad.ipadData.ipad_settings != null)
    {
        storeEvents.activityConfiguration.ipad_settings = storeIpad.ipadData.ipad_settings.id.toString()
    }
    else
    {
        storeEvents.activityConfiguration.ipad_settings = "0"
    }

    templateOldValue.value = storeEvents.activityConfiguration.ipad_settings

    storeEvents.activityConfiguration.duration = storeIpad.ipadData.duration
    if(storeIpad.ipadData.latitude == null)
    {
        storeEvents.activityConfiguration.latitude = null
        storeEvents.activityConfiguration.longitude = null
        firstSave.value = true
    }
    else
    {
        storeEvents.activityConfiguration.latitude = parseFloat(storeIpad.ipadData.latitude)
        storeEvents.activityConfiguration.longitude = parseFloat(storeIpad.ipadData.longitude)
        addMarker({lat: storeEvents.activityConfiguration.latitude, lng: storeEvents.activityConfiguration.longitude})
    }
    storeEvents.activityConfiguration.type = storeIpad.ipadData.type
    if (storeIpad.ipadData.map_config_id) {
        setSelectedConfigId(storeIpad.ipadData.map_config_id)
        storeEvents.activityConfiguration.map_config_id = storeIpad.ipadData.map_config_id
    }
}
onMounted( async() => {
    storeEvents.activityConfiguration = {
        latitude: null,
        longitude: null,
        ipad_settings: null,
        duration: null,
        type: "MAP",
        activity_formRef: createFormRef(),
        map_config_id: null,
    }
    if(route.params.api_key)
    {
        await fetchPromiseAll([
            storeIpad.getIpadHuntData(String(route.params.api_key)),
            storeContent.getAllContent("ipad_settings")
        ])
        initIpadSetting.value = storeEvents.activityConfiguration.ipad_settings
        durationMinutes.value = Math.trunc(storeIpad.ipadData.duration / 60)
    }
    else
    {
        storeContent.getAllContent("ipad_settings")
    }
    
    if (mapContainer.value) {
       await loadMap();
        if(route.params.api_key)
        {
            loadIpadForm();
        }
        if (storeEvents.activityConfiguration.ipad_settings) {
            await fetchConfigs(storeEvents.activityConfiguration.ipad_settings)
        }
    }
})

</script>

<style lang="scss" scoped>
.components-list {
	grid-template-columns: none;
}
.card-css {
    background-color: var(--bg-color);
}
</style>
